import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import { locationHubService } from '../../services/locationHubService';
import companyService from '../../services/CompanyService';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert';
import { MapPin, ArrowLeft, Pencil } from 'lucide-react';
import Button from '../common/Button.js';
import StateSelectField from '../common/StateSelectField.js';
import { destinationTypeLabel } from '../../utils/destinationType.js';
import { downloadHubAttendanceQr } from './downloadHubAttendanceQr.js';
import { canScanQrAttendance, hasInternalHubDispatchControls } from '../../utils/networkMode.js';
import HubGpsFields from './HubGpsFields.js';
import DestinationHubMultiSelect from './DestinationHubMultiSelect.js';
import HubDeliverySettings, { slotsFromDeliveryTime } from './HubDeliverySettings.js';
import HubPrintSettings from './HubPrintSettings.js';
import HubBulkLoadSetting from './HubBulkLoadSetting.js';
import companySettingsService from '../../services/companySettingsService.js';
import { isLrShipmentFormat } from '../../utils/shipmentIdFormat.js';

const readOnlyClass =
  'w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-100 text-sm sm:text-base text-gray-700 cursor-not-allowed';

const ViewLocationHub = () => {
  const { hubId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [branchUpiEnabled, setBranchUpiEnabled] = useState(Boolean(user?.branch_upi_qr_enabled));
  const showDispatchControls = user?.role === 'System Admin' || hasInternalHubDispatchControls(user);
  const [loading, setLoading] = useState(true);
  const [hubData, setHubData] = useState(null);
  const [destinationHubOptions, setDestinationHubOptions] = useState([]);
  const [showLrBranchCode, setShowLrBranchCode] = useState(false);

  useEffect(() => {
    const companyId = hubData?.internal_company_id || user?.company_internal_id;
    if (!companyId) return undefined;
    let cancelled = false;
    companyService.getRecord(companyId)
      .then((res) => {
        if (!cancelled) setBranchUpiEnabled(Boolean(res?.data?.branch_upi_qr_enabled));
      })
      .catch(() => {
        if (!cancelled) setBranchUpiEnabled(Boolean(user?.branch_upi_qr_enabled));
      });
    return () => { cancelled = true; };
  }, [hubData?.internal_company_id, user?.company_internal_id, user?.branch_upi_qr_enabled]);

  useEffect(() => {
    let cancelled = false;

    const fetchHub = async () => {
      try {
        setLoading(true);
        const response = await locationHubService.getHub(hubId);
        const hub = response.data.hub || response.data.data;

        if (!hub) {
          Alert.error('Destination hub not found');
          return;
        }
        if (cancelled) return;
        setHubData(hub);

        const filters = {};
        if (hub.internal_company_id) filters.company_id = hub.internal_company_id;
        const hubsRes = await locationHubService.getHubsForSelect(filters);
        if (!cancelled) {
          setDestinationHubOptions(hubsRes.data.hubs || hubsRes.data.data || []);
        }
        try {
          const settingsRes = await companySettingsService.getSettings(
            user?.role === 'System Admin' ? hub.internal_company_id : null,
          );
          if (!cancelled) {
            setShowLrBranchCode(
              !!settingsRes?.data?.lr_branch_code_required
              || isLrShipmentFormat(settingsRes?.data?.shipment_id_format),
            );
          }
        } catch {
          if (!cancelled) setShowLrBranchCode(false);
        }
      } catch (error) {
        console.error('Error fetching hub:', error);
        if (!cancelled) {
          Alert.error(error.response?.data?.error || 'Failed to load destination hub');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHub();
    return () => { cancelled = true; };
  }, [hubId, user?.role]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading destination hub..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!hubData) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow p-6 text-center">
            <p className="text-gray-600 mb-4">Destination hub not found</p>
            <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/location-hubs')}>
              Back to Location Hubs
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isBranch = hubData.destination_type === 'branch_destination';
  const dispatchTypes = Array.isArray(hubData.dispatch_types)
    ? hubData.dispatch_types
    : String(hubData.dispatch_types || 'OGCL').split(',').map((item) => item.trim()).filter(Boolean);
  const destinationIds = (hubData.destination_location_hub_ids || []).map(String);
  const settlementIds = (hubData.settlement_location_hub_ids || []).map(String);
  const parentLabel = hubData.parent_hub_name
    ? `${hubData.parent_hub_name}${hubData.parent_hub_code ? ` (${hubData.parent_hub_code})` : ''}`
    : '';
  const gpsForm = {
    latitude: hubData.latitude ?? '',
    longitude: hubData.longitude ?? '',
  };
  const deliverySlots = slotsFromDeliveryTime(hubData.delivery_time);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="flex w-full">
              <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                <MapPin className="w-6 h-6 text-secondary" />
                {isBranch ? 'View Branch' : 'View Hub'}
              </h1>
            </div>
            <div className="flex justify-end w-full">
              <div className="flex items-center gap-3 sm:gap-4">
                {canScanQrAttendance(user) && (
                  <Button variant="secondary" onClick={() => downloadHubAttendanceQr(hubData)}>
                    Download Attendance QR
                  </Button>
                )}
                <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/location-hubs')}>
                  Back to Location Hubs
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
          <div>
            <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Hub Information
            </h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Hub/Branch <span className="text-red-500">*</span>
              </label>
              <input type="text" value={hubData.location || ''} readOnly className={readOnlyClass} />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Location Hub Code <span className="text-red-500">*</span>
              </label>
              <input type="text" value={hubData.location_code || ''} readOnly className={`${readOnlyClass} uppercase`} />
            </div>

            {showLrBranchCode && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                LR branch code <span className="text-gray-400 font-normal">(00-99)</span>
              </label>
              <input type="text" value={hubData.lr_branch_code || ''} readOnly className={readOnlyClass} />
            </div>
            )}

            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Parent Hub <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={isBranch ? (parentLabel || '—') : 'Not used for Hub'}
                readOnly
                className={readOnlyClass}
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Destination Type <span className="text-red-500">*</span>
              </label>
              <SelectField
                variant="form"
                value={hubData.destination_type || ''}
                onChange={() => {}}
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-100 text-sm sm:text-base cursor-not-allowed"
                disabled
              >
                <option value={hubData.destination_type || ''}>
                  {hubData.destination_type_label || destinationTypeLabel(hubData.destination_type) || '—'}
                </option>
              </SelectField>
            </div>

            {/* Commission % — hidden for now
            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Commission %
              </label>
              <input
                type="text"
                value={hubData.commission_percentage != null ? hubData.commission_percentage : ''}
                readOnly
                className={readOnlyClass}
              />
            </div>
            */}

            {!!user?.labour_reserve_enabled && (
              <>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                    Loading reserve %
                  </label>
                  <input
                    type="text"
                    value={
                      hubData.loading_labour_reserve_percent != null
                        ? hubData.loading_labour_reserve_percent
                        : (hubData.labour_reserve_percent != null ? hubData.labour_reserve_percent : '')
                    }
                    readOnly
                    className={readOnlyClass}
                  />
                  <p className="mt-1 text-[11px] text-gray-400">Booking agent at this hub. Advance % paid to cashbook on ML create; remainder held as reserve.</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                    Unloading reserve %
                  </label>
                  <input
                    type="text"
                    value={
                      hubData.unloading_labour_reserve_percent != null
                        ? hubData.unloading_labour_reserve_percent
                        : (hubData.labour_reserve_percent != null ? hubData.labour_reserve_percent : '')
                    }
                    readOnly
                    className={readOnlyClass}
                  />
                  <p className="mt-1 text-[11px] text-gray-400">Delivery agent at this hub. Advance % paid to cashbook on ML create; remainder held as reserve.</p>
                </div>
              </>
            )}

            <HubBulkLoadSetting enabled={!!hubData.bulk_load} readOnly />

            <div className="col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Address <span className="text-red-500">*</span>
              </label>
              <input type="text" value={hubData.address || ''} readOnly className={readOnlyClass} />
            </div>

            <StateSelectField
              label="State"
              required
              disabled
              value={hubData.state || ''}
              onChange={() => {}}
            />

            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Branch Contact Number <span className="text-red-500">*</span>
              </label>
              <input type="text" value={hubData.location_hub_contact || ''} readOnly className={readOnlyClass} />
            </div>

            {branchUpiEnabled && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Branch UPI ID
              </label>
              <input type="text" value={hubData.branch_upi_id || ''} readOnly className={readOnlyClass} />
            </div>
            )}

            <div className="col-span-2 sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Destination Location Hubs
              </label>
              <DestinationHubMultiSelect
                useNumberCode={showLrBranchCode}
                hubs={destinationHubOptions}
                value={destinationIds}
                onChange={() => {}}
                disabled
                placeholder={destinationIds.length === 0 ? 'None selected' : 'Select destination hubs'}
              />
              <p className="mt-1 text-xs text-gray-500">
                Only these hubs (and their branches) appear in Booking / ML To when this hub is the source.
              </p>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Rate / KG (₹) <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={hubData.rate_per_kg != null && hubData.rate_per_kg !== '' ? hubData.rate_per_kg : ''}
                readOnly
                className={readOnlyClass}
              />
              <p className="mt-1 text-xs text-gray-400">Used for weight-based freight when this hub is the receiver</p>
            </div>

            <div className="col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Settle to hubs</label>
              <DestinationHubMultiSelect
                useNumberCode={showLrBranchCode}
                hubs={destinationHubOptions}
                value={settlementIds}
                onChange={() => {}}
                disabled
                placeholder="Company default — tick hubs to override"
              />
              <p className="mt-1 text-[11px] text-gray-400">
                Leave empty to use Company Settings. Tick one or more hubs. The agent chooses one of the ticked hubs when sending money.
              </p>
            </div>

            <HubPrintSettings
              bookingReceiptSize={hubData.booking_receipt_size || 'large'}
              deliveryReceiptSize={hubData.delivery_receipt_size || 'large'}
              printQr={!!hubData.print_qr_with_booking_receipt}
              readOnly
            />
            <HubDeliverySettings
              deliveryTime1={deliverySlots.slots[0].time}
              period1={deliverySlots.slots[0].period}
              deliveryTime2={deliverySlots.slots[1].time}
              period2={deliverySlots.slots[1].period}
              legacyText={deliverySlots.legacy}
              detailsRequired={hubData.delivery_details_required !== false}
              readOnly
            />

            <HubGpsFields
              formData={gpsForm}
              onChange={() => {}}
              loading
              onUseCurrent={() => {}}
            />

            {showDispatchControls && (
              <div className="col-span-2 sm:col-span-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <h4 className="font-semibold text-secondary mb-3">Hub Dispatch Capabilities</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Ownership Type</label>
                    <SelectField
                      variant="form"
                      value={hubData.ownership_type || 'OWN'}
                      onChange={() => {}}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-gray-100"
                    >
                      <option value="OWN">Own</option>
                      <option value="AGENT">Agent</option>
                    </SelectField>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-xs sm:text-sm font-medium text-secondary mb-2">Dispatch Types <span className="text-red-500">*</span></p>
                    <div className="flex gap-5">
                      {['OGCL', 'ITCL'].map((type) => (
                        <label key={type} className="flex items-center gap-2 text-sm text-gray-700">
                          <input type="checkbox" checked={dispatchTypes.includes(type)} readOnly disabled />
                          {type}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
                  {[
                    ['allow_paid', 'Allow Paid'],
                    ['allow_to_pay', 'Allow To Pay'],
                    ['allow_on_account', 'Allow Credit'],
                    ['allow_delivery', 'Allow Delivery'],
                    ['allow_booking', 'Allow Booking'],
                    ['allow_dispatch', 'Allow Dispatch'],
                  ].map(([name, label]) => (
                    <label key={name} className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={!!hubData[name]} readOnly disabled />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="primary" icon={Pencil} onClick={() => navigate(`/location-hubs/edit/${hubId}`)}>
              Edit Hub
            </Button>
            <Button variant="secondary" onClick={() => navigate('/location-hubs')}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ViewLocationHub;
